'use client';

import { c as _c } from "react/compiler-runtime";
import { isNativePushActive } from '@/features/onboarding/hooks/use-native-push-subscription-status';
import { SESSION_EXPIRED_ENTRYPOINT } from '@/features/onboarding/onboarding-constants';
import { useOnlineStatus } from '@/hooks/use-online-status';
import { flushPersonalData } from '@/lib/flush-personal-data';
import { forgetOnboardingComplete, ONBOARDING_COMPLETE_KEY } from '@/lib/onboarding-complete';
import { Cookie } from '@/types/types';
import { replaceLocation } from '@/utils/assign-location';
import { isCookieTrue } from '@/utils/cookie-utils';
import { ANSWERED_BY_SERVICE_WORKER_HEADER } from '@/utils/service-worker-messages';
import { isNativeAppWebView } from '@/utils/standalone-check';
import { useEffect, useRef } from 'react';
const isOnboardingComplete = (): boolean => {
  try {
    return localStorage.getItem(ONBOARDING_COMPLETE_KEY) === 'true';
  } catch {
    return false;
  }
};

/**
 * Asks the server whether the session is still valid. `undefined` while it cannot tell: no
 * answer, an error, or an answer the service worker made up from its cache.
 */
const fetchSessionVerdict = async (): Promise<'valid' | 'expired' | undefined> => {
  try {
    const response = await fetch('/api/auth/session', {
      cache: 'no-store'
    });
    if (!response.ok || response.headers.has(ANSWERED_BY_SERVICE_WORKER_HEADER)) return undefined;
    const session = (await response.json()) as {
      user?: unknown;
    } | null;
    return session?.user === undefined || session.user === null ? 'expired' : 'valid';
  } catch {
    return undefined;
  }
};

/**
 * The onboarding's start-up checks, run in the background of the app.
 *
 * A start whose onboarding is done skips the entrypoint (see `EntrypointFastPath`), so the
 * checks it ran on every start run here instead, without holding up the app:
 *
 * - **Session.** A logged-in user whose session has expired goes back to the entrypoint, which
 *   shows the login. Only the server's own answer counts: on wifi without an uplink the service
 *   worker answers from its cache, and the check waits until the network answers again, so a
 *   bad connection never logs anybody out.
 * - **Push.** Push permission withdrawn in the system settings, or a lost token, makes the next
 *   start show the push step again, as it did before; the current visit is not interrupted.
 */
export const useOnboardingRecheck = () => {
  const $ = _c(4);
  const isOnline = useOnlineStatus();
  const hasCheckedSession = useRef(false);
  let t0;
  let t1;
  if ($[0] !== isOnline) {
    t0 = () => {
      if (!isOnline || hasCheckedSession.current || !isOnboardingComplete()) {
        return;
      }
      if (isCookieTrue(Cookie.HAS_SKIPPED_AUTH)) {
        return;
      }
      let isCancelled = false;
      fetchSessionVerdict().then(verdict => {
        if (isCancelled || verdict === undefined) {
          return;
        }
        hasCheckedSession.current = true;
        if (verdict === "valid") {
          return;
        }
        flushPersonalData({
          keepUnsentChatMessages: true
        });
        replaceLocation(SESSION_EXPIRED_ENTRYPOINT);
      });
      return () => {
        isCancelled = true;
      };
    };
    t1 = [isOnline];
    $[0] = isOnline;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useEffect(_temp2, t2);
};
function _temp(event) {
  const detail = (event as CustomEvent<{
    type?: string;
    payload?: Record<string, unknown>;
  } | null>).detail ?? {};
  if (detail.type !== "native-push-status") {
    return;
  }
  if (!isNativePushActive(detail.payload ?? {})) {
    forgetOnboardingComplete();
  }
}
function _temp2() {
  if (!isOnboardingComplete() || isCookieTrue(Cookie.SKIP_PUSH_NOTIFICATION)) {
    return;
  }
  if (!isNativeAppWebView() && typeof Notification !== "undefined" && Notification.permission !== "granted") {
    forgetOnboardingComplete();
  }
  const handleNativePushEvent = _temp;
  globalThis.addEventListener("app-webview-native-push-event", handleNativePushEvent);
  return () => {
    globalThis.removeEventListener("app-webview-native-push-event", handleNativePushEvent);
  };
}